import React, { useState, useEffect } from 'react';
import axios from 'axios';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handle = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);
    return isMobile;
};

const Journal = ({ token }) => {
    const isMobile = useIsMobile();
    const [entries, setEntries] = useState([]);
    const [library, setLibrary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLogModal, setShowLogModal] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [editingEntry, setEditingEntry] = useState(null);

    const API = process.env.REACT_APP_API_URL;

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = async () => {
        try {
            const [entriesRes, libraryRes] = await Promise.all([
                axios.get(`${API}/api/journal`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/api/library`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            setEntries(entriesRes.data);
            setLibrary(libraryRes.data);
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        try {
            await axios.delete(`${API}/api/journal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchAll();
        } catch (err) { console.error(err); }
    };

    const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

    const totalWears = entries.length;
    const wearCount = {};
    let totalCompliments = 0;
    let totalLongevity = 0;
    let longevityCount = 0;

    entries.forEach(e => {
        const key = `${e.brand} ${e.name}`;
        wearCount[key] = (wearCount[key] || 0) + 1;
        if (e.compliments) totalCompliments += e.compliments;
        if (e.longevity_rating) { totalLongevity += e.longevity_rating; longevityCount++; }
    });

    const mostWorn = Object.entries(wearCount).sort((a, b) => b[1] - a[1])[0];
    const avgLongevity = longevityCount > 0 ? (totalLongevity / longevityCount).toFixed(1) : null;
    const longevityLabels = ['', '2hrs', '4hrs', '6hrs', 'All day', 'Next day'];
    const sillageLabels = ['', 'Personal', 'Close range', 'Nearby', 'Room filler', 'Trail'];

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const ratingDots = (value, max = 5) =>
        Array.from({ length: max }, (_, i) => (
            <span key={i} style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: i < value ? '#c9a96e' : '#2a2a2a', marginRight: '5px' }} />
        ));

    const styles = {
        container: { minHeight: '100vh', padding: isMobile ? '100px 16px 48px' : '120px 48px 48px', maxWidth: '1200px', margin: '0 auto' },
        header: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: isMobile ? '20px' : '0', marginBottom: '48px', borderBottom: '1px solid #2a2a2a', paddingBottom: '32px' },
        eyebrow: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '12px' },
        title: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: 300, color: '#f5f0eb' },
        addButton: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a', background: '#c9a96e', border: 'none', padding: '14px 32px', cursor: 'pointer', width: isMobile ? '100%' : 'auto' },
        statsRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1px', background: '#1a1a1a', border: '1px solid #1a1a1a', marginBottom: '48px' },
        stat: { background: '#0f0f0f', padding: isMobile ? '20px 16px' : '28px 32px' },
        statLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#4a4a4a', marginBottom: '10px' },
        statValue: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.8rem' : '2.2rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, marginBottom: '6px' },
        statSub: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#6b6460', marginTop: '4px', lineHeight: 1.4 },
        sectionLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '24px' },
        entryList: { border: '1px solid #1e1e1e' },
        entryCollapsed: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr auto' : '140px 1fr auto',
            alignItems: 'center',
            borderBottom: '1px solid #1e1e1e',
            padding: isMobile ? '18px 16px' : '22px 32px',
            background: '#111111',
            gap: isMobile ? '12px' : '32px',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
        },
        entryExpanded: { borderBottom: '1px solid #1e1e1e', background: '#0f0f0f', cursor: 'pointer' },
        entryDate: { fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 300, color: '#4a4a4a', letterSpacing: '0.05em' },
        entryBrand: { fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '3px' },
        entryName: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: 300, color: '#f5f0eb' },
        tag: { fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b6460', border: '1px solid #222', padding: '3px 8px' },
        deleteBtn: { background: 'none', border: 'none', color: '#2a2a2a', cursor: 'pointer', fontSize: '0.9rem', padding: '4px 8px', transition: 'color 0.2s ease' },
        editBtn: { background: 'none', border: '1px solid #2a2a2a', color: '#6b6460', cursor: 'pointer', fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '5px 12px', transition: 'all 0.2s ease' },
        expandedHeader: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr auto' : '140px 1fr auto',
            alignItems: 'center',
            padding: isMobile ? '18px 16px' : '22px 32px',
            gap: isMobile ? '12px' : '32px',
            borderBottom: '1px solid #1a1a1a',
        },
        expandedBody: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1px 1fr 1px 1fr',
        },
        expandedCol: { padding: isMobile ? '20px 16px' : '28px 32px', borderBottom: isMobile ? '1px solid #1a1a1a' : 'none' },
        expandedDivider: { background: '#1a1a1a', display: isMobile ? 'none' : 'block' },
        expandedLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#3a3a3a', marginBottom: '10px' },
        expandedValue: { fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', fontWeight: 300, color: '#a09890' },
        expandedNotes: { padding: isMobile ? '16px' : '20px 32px', borderTop: '1px solid #1a1a1a', fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontWeight: 300, color: '#6b6460', fontStyle: 'italic', lineHeight: 1.6 },
        empty: { textAlign: 'center', padding: '80px 0' },
        emptyTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', fontStyle: 'italic' },
        emptyText: { fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: '#a09890', marginBottom: '40px' },
    };

    if (loading) return (
        <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>Loading your journal...</p>
        </div>
    );

    const JournalEntry = ({ entry }) => {
        const isExpanded = expandedId === entry.id;
        const [hovered, setHovered] = useState(false);

        if (!isExpanded) {
            return (
                <div
                    style={{ ...styles.entryCollapsed, background: hovered ? '#161616' : '#111111' }}
                    onClick={() => toggleExpand(entry.id)}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <div>
                        {isMobile && <p style={{ ...styles.entryDate, marginBottom: '6px' }}>{formatDate(entry.date_worn)}</p>}
                        {!isMobile && <p style={styles.entryDate}>{formatDate(entry.date_worn)}</p>}
                        <p style={styles.entryBrand}>{entry.brand}</p>
                        <p style={styles.entryName}>{entry.name}</p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {entry.occasion && <span style={styles.tag}>{entry.occasion}</span>}
                            {entry.mood && <span style={styles.tag}>{entry.mood}</span>}
                            {!isMobile && entry.weather && <span style={styles.tag}>{entry.weather}</span>}
                            {entry.sprays > 0 && <span style={styles.tag}>{entry.sprays} spray{entry.sprays !== 1 ? 's' : ''}</span>}
                            {entry.compliments > 0 && <span style={styles.tag}>{entry.compliments} compliment{entry.compliments !== 1 ? 's' : ''}</span>}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#2a2a2a' }}>▼</span>
                        <button style={styles.deleteBtn} onClick={(e) => handleDelete(entry.id, e)} onMouseEnter={e => e.currentTarget.style.color = '#e07070'} onMouseLeave={e => e.currentTarget.style.color = '#2a2a2a'}>✕</button>
                    </div>
                </div>
            );
        }

        return (
            <div style={styles.entryExpanded} onClick={() => toggleExpand(entry.id)}>
                <div style={styles.expandedHeader}>
                    <div>
                        {isMobile && <p style={{ ...styles.entryDate, color: '#c9a96e', marginBottom: '4px' }}>{formatDate(entry.date_worn)}</p>}
                        {!isMobile && <p style={{ ...styles.entryDate, color: '#c9a96e' }}>{formatDate(entry.date_worn)}</p>}
                        <p style={styles.entryBrand}>{entry.brand}</p>
                        <p style={{ ...styles.entryName, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>{entry.name}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button style={styles.editBtn} onClick={(e) => { e.stopPropagation(); setEditingEntry(entry); }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#6b6460'; }}>Edit</button>
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#c9a96e' }}>▲</span>
                        <button style={styles.deleteBtn} onClick={(e) => handleDelete(entry.id, e)} onMouseEnter={e => e.currentTarget.style.color = '#e07070'} onMouseLeave={e => e.currentTarget.style.color = '#2a2a2a'}>✕</button>
                    </div>
                </div>

                <div style={styles.expandedBody}>
                    <div style={styles.expandedCol}>
                        <p style={styles.expandedLabel}>Context</p>
                        {entry.occasion && <div style={{ marginBottom: '14px' }}><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Occasion</p><p style={styles.expandedValue}>{entry.occasion}</p></div>}
                        {entry.mood && <div style={{ marginBottom: '14px' }}><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Mood</p><p style={styles.expandedValue}>{entry.mood}</p></div>}
                        {entry.sprays > 0 && <div style={{ marginBottom: '14px' }}><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Sprays</p><p style={styles.expandedValue}>{entry.sprays}</p></div>}
                        {entry.compliments > 0 && <div><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Compliments</p><p style={{ ...styles.expandedValue, color: '#c9a96e', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem' }}>{entry.compliments}</p></div>}
                    </div>

                    <div style={styles.expandedDivider} />

                    <div style={styles.expandedCol}>
                        <p style={styles.expandedLabel}>Conditions</p>
                        {entry.weather && <div style={{ marginBottom: '14px' }}><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Weather</p><p style={styles.expandedValue}>{entry.weather}</p></div>}
                        {entry.temperature && <div><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '4px' }}>Temperature</p><p style={styles.expandedValue}>{entry.temperature}°C</p></div>}
                    </div>

                    <div style={styles.expandedDivider} />

                    <div style={styles.expandedCol}>
                        <p style={styles.expandedLabel}>Performance</p>
                        {entry.longevity_rating && <div style={{ marginBottom: '16px' }}><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '6px' }}>Longevity</p><div style={{ marginBottom: '4px' }}>{ratingDots(entry.longevity_rating)}</div><p style={{ ...styles.expandedValue, fontSize: '0.7rem', color: '#4a4a4a' }}>{longevityLabels[entry.longevity_rating]}</p></div>}
                        {entry.sillage_rating && <div><p style={{ ...styles.expandedLabel, color: '#2a2a2a', marginBottom: '6px' }}>Projection</p><div style={{ marginBottom: '4px' }}>{ratingDots(entry.sillage_rating)}</div><p style={{ ...styles.expandedValue, fontSize: '0.7rem', color: '#4a4a4a' }}>{sillageLabels[entry.sillage_rating]}</p></div>}
                    </div>
                </div>

                {entry.notes && <div style={styles.expandedNotes}>"{entry.notes}"</div>}
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <p style={styles.eyebrow}>Your History</p>
                    <h1 style={styles.title}>Journal</h1>
                </div>
                <button style={styles.addButton} onClick={() => setShowLogModal(true)}>Log a Wear</button>
            </div>

            {entries.length > 0 && (
                <div style={styles.statsRow}>
                    <div style={styles.stat}>
                        <p style={styles.statLabel}>Total Wears</p>
                        <p style={styles.statValue}>{totalWears}</p>
                    </div>
                    <div style={styles.stat}>
                        <p style={styles.statLabel}>Fragrances Worn</p>
                        <p style={styles.statValue}>{Object.keys(wearCount).length}</p>
                    </div>
                    <div style={styles.stat}>
                        <p style={styles.statLabel}>Most Worn</p>
                        {mostWorn ? (<><p style={styles.statValue}>{mostWorn[1]}×</p><p style={styles.statSub}>{mostWorn[0]}</p></>) : <p style={styles.statValue}>—</p>}
                    </div>
                    <div style={styles.stat}>
                        <p style={styles.statLabel}>Avg. Longevity</p>
                        <p style={styles.statValue}>{avgLongevity ? longevityLabels[Math.round(avgLongevity)] : '—'}</p>
                        {avgLongevity && <p style={styles.statSub}>across {longevityCount} rated wear{longevityCount !== 1 ? 's' : ''}</p>}
                    </div>
                    <div style={{ ...styles.stat, gridColumn: '1 / -1', paddingTop: '12px', paddingBottom: '12px' }}>
                        <p style={styles.statLabel}>Total Compliments</p>
                        <p style={{ ...styles.statValue, fontSize: '1.4rem', display: 'inline', marginRight: '12px' }}>{totalCompliments}</p>
                        <span style={styles.statSub}>received across all wears</span>
                    </div>
                </div>
            )}

            {entries.length === 0 ? (
                <div style={styles.empty}>
                    <p style={styles.emptyTitle}>Your story begins here</p>
                    <p style={styles.emptyText}>Log your first wear to start tracking your fragrance journey</p>
                    <button style={styles.addButton} onClick={() => setShowLogModal(true)}>Log Your First Wear</button>
                </div>
            ) : (
                <>
                    <p style={styles.sectionLabel}>Wear History</p>
                    <div style={styles.entryList}>
                        {entries.map(entry => <JournalEntry key={entry.id} entry={entry} />)}
                    </div>
                </>
            )}

            {showLogModal && (
                <LogWearModal
                    token={token}
                    library={library}
                    onClose={() => setShowLogModal(false)}
                    onSuccess={() => { fetchAll(); setShowLogModal(false); }}
                />
            )}
            {editingEntry && (
                <EditWearModal
                    entry={editingEntry}
                    token={token}
                    library={library}
                    onClose={() => setEditingEntry(null)}
                    onSuccess={() => { fetchAll(); setEditingEntry(null); }}
                />
            )}
        </div>
    );
};

// ── Edit Wear Modal ──────────────────────────────────────────────
const EditWearModal = ({ entry, token, library, onClose, onSuccess }) => {
    const API = process.env.REACT_APP_API_URL;
    const [form, setForm] = useState({
        date_worn: entry.date_worn?.split('T')[0] || '',
        occasion: entry.occasion || '',
        weather: entry.weather || '',
        temperature: entry.temperature || '',
        longevity_rating: entry.longevity_rating || '',
        sillage_rating: entry.sillage_rating || '',
        compliments: entry.compliments || '',
        mood: entry.mood || '',
        notes: entry.notes || '',
        sprays: entry.sprays || 4,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    // Find library item for bottle preview
    const libraryItem = library.find(l => l.fragrance_id === entry.fragrance_id);

    const calculateNewPct = () => {
        if (!libraryItem?.bottle_size_ml) return null;
        const sprayVolume = 0.1;
        const totalVolume = libraryItem.bottle_size_ml;
        const currentVolume = (libraryItem.bottle_percentage / 100) * totalVolume;
        const newVolume = Math.max(0, currentVolume - (form.sprays * sprayVolume));
        return Math.round((newVolume / totalVolume) * 100);
    };

    const newPct = calculateNewPct();

    const handleSubmit = async () => {
        setLoading(true); setError('');
        try {
            await axios.patch(`${API}/api/journal/${entry.id}`, {
                ...form,
                temperature: form.temperature ? parseInt(form.temperature) : null,
                longevity_rating: form.longevity_rating ? parseInt(form.longevity_rating) : null,
                sillage_rating: form.sillage_rating ? parseInt(form.sillage_rating) : null,
                compliments: form.compliments ? parseInt(form.compliments) : null,
                sprays: form.sprays ? parseInt(form.sprays) : null,
            }, { headers: { Authorization: `Bearer ${token}` } });

            // Update bottle percentage if we can calculate it
            if (newPct !== null && libraryItem) {
                await axios.patch(
                    `${API}/api/library/${libraryItem.id}`,
                    { bottle_percentage: newPct },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            onSuccess();
        } catch (err) { setError(err.response?.data?.error || 'Something went wrong'); }
        setLoading(false);
    };

    const s = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' },
        modal: { background: '#111111', border: '1px solid #2a2a2a', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', padding: '32px 24px' },
        close: { position: 'absolute', top: '20px', right: '24px', background: 'none', border: 'none', color: '#a09890', fontSize: '1rem', cursor: 'pointer' },
        eyebrow: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '12px' },
        title: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '4px' },
        subtitle: { fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', color: '#4a4a4a', letterSpacing: '0.05em', marginBottom: '32px' },
        label: { fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a09890', marginBottom: '8px', display: 'block' },
        input: { width: '100%', background: '#1a1a1a', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, marginBottom: '20px', outline: 'none', boxSizing: 'border-box' },
        select: { width: '100%', background: '#1a1a1a', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, marginBottom: '20px', outline: 'none', cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' },
        row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
        button: { width: '100%', background: '#c9a96e', border: 'none', padding: '16px', color: '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '8px' },
        error: { color: '#e07070', fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', marginBottom: '16px' },
        divider: { border: 'none', borderTop: '1px solid #2a2a2a', margin: '24px 0' },
        sectionLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#665e58', marginBottom: '16px' },
        sprayRow: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' },
        sprayBtn: { width: '36px', height: '36px', background: '#1a1a1a', border: '1px solid #2a2a2a', color: '#f5f0eb', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        sprayCount: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#c9a96e', minWidth: '36px', textAlign: 'center' },
        sprayLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', fontWeight: 300, color: '#665e58' },
        bottlePreview: { background: '#0e0e0e', border: '1px solid #2a2a2a', padding: '14px', marginBottom: '20px' },
        bottleLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#665e58', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' },
        bottleBar: { height: '2px', background: '#2a2a2a', marginBottom: '6px' },
        bottleBarFill: (pct) => ({ height: '2px', background: pct > 30 ? '#c9a96e' : '#e07070', width: `${pct}%`, transition: 'width 0.3s ease' }),
        bottleChange: { fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 300, color: '#a09890', textAlign: 'right' },
    };

    return (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={s.modal}>
                <button style={s.close} onClick={onClose}>✕</button>
                <p style={s.eyebrow}>Journal</p>
                <h2 style={s.title}>Edit Entry</h2>
                <p style={s.subtitle}>{entry.brand} — {entry.name}</p>

                <label style={s.label}>Date Worn</label>
                <input style={s.input} type="date" value={form.date_worn} onChange={e => set('date_worn', e.target.value)} />

                {/* Spray counter */}
                <label style={s.label}>Sprays</label>
                <div style={s.sprayRow}>
                    <button style={s.sprayBtn} onClick={() => set('sprays', Math.max(1, form.sprays - 1))}>−</button>
                    <span style={s.sprayCount}>{form.sprays}</span>
                    <button style={s.sprayBtn} onClick={() => set('sprays', Math.min(20, form.sprays + 1))}>+</button>
                    <span style={s.sprayLabel}>sprays</span>
                </div>

                {/* Bottle preview */}
                {newPct !== null && (
                    <div style={s.bottlePreview}>
                        <div style={s.bottleLabel}>
                            <span>Bottle Level</span>
                            <span style={{ color: newPct > 30 ? '#c9a96e' : '#e07070' }}>{newPct}%</span>
                        </div>
                        <div style={s.bottleBar}><div style={s.bottleBarFill(newPct)} /></div>
                        <p style={s.bottleChange}>{libraryItem.bottle_percentage}% → {newPct}% after {form.sprays} spray{form.sprays !== 1 ? 's' : ''}</p>
                    </div>
                )}

                <div style={s.row}>
                    <div><label style={s.label}>Occasion</label><select style={s.select} value={form.occasion} onChange={e => set('occasion', e.target.value)}><option value="">Select...</option><option>Casual</option><option>Office</option><option>Evening</option><option>Date</option><option>Sport</option><option>Night Out</option></select></div>
                    <div><label style={s.label}>Mood</label><select style={s.select} value={form.mood} onChange={e => set('mood', e.target.value)}><option value="">Select...</option><option>Confident</option><option>Relaxed</option><option>Romantic</option><option>Energetic</option><option>Professional</option><option>Adventurous</option></select></div>
                </div>
                <hr style={s.divider} />
                <p style={s.sectionLabel}>Conditions</p>
                <div style={s.row}>
                    <div><label style={s.label}>Weather</label><select style={s.select} value={form.weather} onChange={e => set('weather', e.target.value)}><option value="">Select...</option><option>Sunny</option><option>Cloudy</option><option>Rainy</option><option>Snowy</option><option>Humid</option><option>Windy</option></select></div>
                    <div><label style={s.label}>Temperature (°C)</label><input style={s.input} type="number" placeholder="e.g. 18" value={form.temperature} onChange={e => set('temperature', e.target.value)} /></div>
                </div>
                <hr style={s.divider} />
                <p style={s.sectionLabel}>Performance</p>
                <div style={s.row}>
                    <div><label style={s.label}>Longevity</label><select style={s.select} value={form.longevity_rating} onChange={e => set('longevity_rating', e.target.value)}><option value="">Select...</option><option value="1">Faded within 2 hours</option><option value="2">Lasted ~4 hours</option><option value="3">Lasted ~6 hours</option><option value="4">Lasted all day</option><option value="5">Still going next morning</option></select></div>
                    <div><label style={s.label}>Projection</label><select style={s.select} value={form.sillage_rating} onChange={e => set('sillage_rating', e.target.value)}><option value="">Select...</option><option value="1">Only I could smell it</option><option value="2">Close range only</option><option value="3">People nearby noticed</option><option value="4">Filled the room</option><option value="5">Left a trail everywhere</option></select></div>
                </div>
                <label style={s.label}>Compliments Received</label>
                <input style={s.input} type="number" min="0" placeholder="How many compliments?" value={form.compliments} onChange={e => set('compliments', e.target.value)} />
                <label style={s.label}>Notes</label>
                <textarea style={{ ...s.input, height: '80px', resize: 'vertical' }} placeholder="How did it wear?" value={form.notes} onChange={e => set('notes', e.target.value)} />
                {error && <p style={s.error}>{error}</p>}
                <button style={s.button} onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
            </div>
        </div>
    );
};

// ── Log Wear Modal ───────────────────────────────────────────────
const LogWearModal = ({ token, library, onClose, onSuccess }) => {
    const today = new Date().toISOString().split('T')[0];
    const [form, setForm] = useState({
        fragrance_id: '', date_worn: today, occasion: '', weather: '',
        temperature: '', longevity_rating: '', sillage_rating: '',
        compliments: '', mood: '', notes: '',
    });
    const [sprays, setSprays] = useState(4);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const API = process.env.REACT_APP_API_URL;
    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    // Find selected library item for bottle preview
    const selectedItem = library.find(l => l.fragrance_id === parseInt(form.fragrance_id));

    const calculateNewPct = () => {
        if (!selectedItem?.bottle_size_ml) return null;
        const sprayVolume = 0.1;
        const totalVolume = selectedItem.bottle_size_ml;
        const currentVolume = (selectedItem.bottle_percentage / 100) * totalVolume;
        const newVolume = Math.max(0, currentVolume - (sprays * sprayVolume));
        return Math.round((newVolume / totalVolume) * 100);
    };

    const newPct = calculateNewPct();

    const handleSubmit = async () => {
        if (!form.fragrance_id) { setError('Please select a fragrance'); return; }
        setLoading(true); setError('');
        try {
            await axios.post(`${API}/api/journal`, {
                ...form,
                sprays,
                completed: true,
                temperature: form.temperature ? parseInt(form.temperature) : null,
                longevity_rating: form.longevity_rating ? parseInt(form.longevity_rating) : null,
                sillage_rating: form.sillage_rating ? parseInt(form.sillage_rating) : null,
                compliments: form.compliments ? parseInt(form.compliments) : null,
            }, { headers: { Authorization: `Bearer ${token}` } });

            // Update bottle percentage if we can calculate it
            if (newPct !== null && selectedItem) {
                await axios.patch(
                    `${API}/api/library/${selectedItem.id}`,
                    { bottle_percentage: newPct },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            onSuccess();
        } catch (err) { setError(err.response?.data?.error || 'Something went wrong'); }
        setLoading(false);
    };

    const s = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' },
        modal: { background: '#111111', border: '1px solid #2a2a2a', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', padding: '32px 24px' },
        close: { position: 'absolute', top: '20px', right: '24px', background: 'none', border: 'none', color: '#a09890', fontSize: '1rem', cursor: 'pointer' },
        eyebrow: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '12px' },
        title: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '32px' },
        label: { fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a09890', marginBottom: '8px', display: 'block' },
        input: { width: '100%', background: '#1a1a1a', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, marginBottom: '20px', outline: 'none', boxSizing: 'border-box' },
        select: { width: '100%', background: '#1a1a1a', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, marginBottom: '20px', outline: 'none', cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' },
        row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
        button: { width: '100%', background: '#c9a96e', border: 'none', padding: '16px', color: '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '8px' },
        error: { color: '#e07070', fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', marginBottom: '16px' },
        divider: { border: 'none', borderTop: '1px solid #2a2a2a', margin: '24px 0' },
        sectionLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#665e58', marginBottom: '16px' },
        sprayRow: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' },
        sprayBtn: { width: '36px', height: '36px', background: '#1a1a1a', border: '1px solid #2a2a2a', color: '#f5f0eb', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        sprayCount: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#c9a96e', minWidth: '36px', textAlign: 'center' },
        sprayLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', fontWeight: 300, color: '#665e58' },
        bottlePreview: { background: '#0e0e0e', border: '1px solid #2a2a2a', padding: '14px', marginBottom: '20px' },
        bottleLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#665e58', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' },
        bottleBar: { height: '2px', background: '#2a2a2a', marginBottom: '6px' },
        bottleBarFill: (pct) => ({ height: '2px', background: pct > 30 ? '#c9a96e' : '#e07070', width: `${pct}%`, transition: 'width 0.3s ease' }),
        bottleChange: { fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 300, color: '#a09890', textAlign: 'right' },
        noSize: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#665e58', fontStyle: 'italic', marginBottom: '20px' },
    };

    return (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={s.modal}>
                <button style={s.close} onClick={onClose}>✕</button>
                <p style={s.eyebrow}>Journal</p>
                <h2 style={s.title}>Log a Wear</h2>

                <label style={s.label}>Fragrance</label>
                <select style={s.select} value={form.fragrance_id} onChange={e => set('fragrance_id', e.target.value)}>
                    <option value="">Select from your library...</option>
                    {library.map(item => <option key={item.fragrance_id} value={item.fragrance_id}>{item.brand} — {item.name}</option>)}
                </select>

                <label style={s.label}>Date Worn</label>
                <input style={s.input} type="date" value={form.date_worn} onChange={e => set('date_worn', e.target.value)} />

                {/* Spray counter */}
                <label style={s.label}>How many sprays?</label>
                <div style={s.sprayRow}>
                    <button style={s.sprayBtn} onClick={() => setSprays(s => Math.max(1, s - 1))}>−</button>
                    <span style={s.sprayCount}>{sprays}</span>
                    <button style={s.sprayBtn} onClick={() => setSprays(s => Math.min(20, s + 1))}>+</button>
                    <span style={s.sprayLabel}>sprays</span>
                </div>

                {/* Bottle preview */}
                {form.fragrance_id && (
                    newPct !== null ? (
                        <div style={s.bottlePreview}>
                            <div style={s.bottleLabel}>
                                <span>Bottle Level</span>
                                <span style={{ color: newPct > 30 ? '#c9a96e' : '#e07070' }}>{newPct}%</span>
                            </div>
                            <div style={s.bottleBar}><div style={s.bottleBarFill(newPct)} /></div>
                            <p style={s.bottleChange}>{selectedItem.bottle_percentage}% → {newPct}% after {sprays} spray{sprays !== 1 ? 's' : ''}</p>
                        </div>
                    ) : (
                        <p style={s.noSize}>Add a bottle size to your library entry to track usage automatically</p>
                    )
                )}

                <div style={s.row}>
                    <div><label style={s.label}>Occasion</label><select style={s.select} value={form.occasion} onChange={e => set('occasion', e.target.value)}><option value="">Select...</option><option>Casual</option><option>Office</option><option>Evening</option><option>Date</option><option>Sport</option><option>Night Out</option></select></div>
                    <div><label style={s.label}>Mood</label><select style={s.select} value={form.mood} onChange={e => set('mood', e.target.value)}><option value="">Select...</option><option>Confident</option><option>Relaxed</option><option>Romantic</option><option>Energetic</option><option>Professional</option><option>Adventurous</option></select></div>
                </div>
                <hr style={s.divider} />
                <p style={s.sectionLabel}>Optional Details</p>
                <div style={s.row}>
                    <div><label style={s.label}>Weather</label><select style={s.select} value={form.weather} onChange={e => set('weather', e.target.value)}><option value="">Select...</option><option>Sunny</option><option>Cloudy</option><option>Rainy</option><option>Snowy</option><option>Humid</option><option>Windy</option></select></div>
                    <div><label style={s.label}>Temperature (°C)</label><input style={s.input} type="number" placeholder="e.g. 18" value={form.temperature} onChange={e => set('temperature', e.target.value)} /></div>
                </div>
                <div style={s.row}>
                    <div><label style={s.label}>Longevity</label><select style={s.select} value={form.longevity_rating} onChange={e => set('longevity_rating', e.target.value)}><option value="">Select...</option><option value="1">Faded within 2 hours</option><option value="2">Lasted ~4 hours</option><option value="3">Lasted ~6 hours</option><option value="4">Lasted all day</option><option value="5">Still going next morning</option></select></div>
                    <div><label style={s.label}>Projection</label><select style={s.select} value={form.sillage_rating} onChange={e => set('sillage_rating', e.target.value)}><option value="">Select...</option><option value="1">Only I could smell it</option><option value="2">Close range only</option><option value="3">People nearby noticed</option><option value="4">Filled the room</option><option value="5">Left a trail everywhere</option></select></div>
                </div>
                <label style={s.label}>Compliments Received</label>
                <input style={s.input} type="number" min="0" placeholder="How many compliments did you get?" value={form.compliments} onChange={e => set('compliments', e.target.value)} />
                <label style={s.label}>Notes</label>
                <textarea style={{ ...s.input, height: '80px', resize: 'vertical' }} placeholder="How did it wear? Any thoughts..." value={form.notes} onChange={e => set('notes', e.target.value)} />
                {error && <p style={s.error}>{error}</p>}
                <button style={s.button} onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : `Save Entry (${sprays} spray${sprays !== 1 ? 's' : ''})`}</button>
            </div>
        </div>
    );
};

export default Journal;